import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Send,
  Bell,
  RefreshCw,
  Settings,
  ArrowLeft,
  Users,
  CheckCircle,
  AlertCircle,
  Copy,
  Bot,
  MessageCircle,
  Megaphone,
  UserPlus
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

// Notification types for Telegram
const TELEGRAM_NOTIFICATIONS = [
  { id: 'new_application', name: 'New Application', description: 'Notify when candidate applies', default: true },
  { id: 'interview_reminder', name: 'Interview Reminder', description: 'Send interview reminders', default: true },
  { id: 'status_update', name: 'Status Updates', description: 'Candidate status changes', default: true },
  { id: 'daily_report', name: 'Daily Report', description: 'Daily recruitment summary', default: false },
];

const TelegramIntegration = ({ onBack }) => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [botInfo, setBotInfo] = useState(null);
  const [chatId, setChatId] = useState('');
  const [notifications, setNotifications] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [recentMessages, setRecentMessages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/telegram/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnected(response.data.connected);
      setBotInfo(response.data.bot);
      setNotifications(response.data.notifications || {});
      if (response.data.connected) {
        fetchSubscribers();
        fetchRecentMessages();
      }
    } catch (error) {
      console.error('Error checking Telegram connection:', error);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/telegram/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(response.data.subscribers || []);
    } catch (error) {
      // Demo data
      setSubscribers([
        { id: '1', name: 'HR Group', type: 'group', members: 15 },
        { id: '2', name: 'Recruitment Team', type: 'group', members: 8 },
        { id: '3', name: 'John (Admin)', type: 'private', members: 1 },
      ]);
    }
  };

  const fetchRecentMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/telegram/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentMessages(response.data.messages || []);
    } catch (error) {
      // Demo data
      setRecentMessages([
        { id: '1', text: '📢 New application: John Doe for Software Engineer', time: new Date(Date.now() - 300000).toISOString() },
        { id: '2', text: '📅 Interview scheduled: Sarah Smith tomorrow at 2 PM', time: new Date(Date.now() - 600000).toISOString() },
        { id: '3', text: '✅ Offer accepted by Mike Johnson', time: new Date(Date.now() - 900000).toISOString() },
      ]);
    }
  };

  const handleConnect = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/integrations/telegram/connect`, {
        chat_id: chatId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Telegram connected successfully');
      setConnected(true);
      setBotInfo(response.data.bot);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Telegram');
    }
  };

  const handleDisconnect = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/telegram/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Telegram disconnected');
      setConnected(false);
      setBotInfo(null);
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleBroadcast = async () => {
    if (!broadcastMessage.trim()) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/telegram/broadcast`, {
        message: broadcastMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Broadcast sent successfully');
      setBroadcastMessage('');
      setShowBroadcast(false);
      fetchRecentMessages();
    } catch (error) {
      toast.error('Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const handleUpdateNotifications = async (notifId, enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/integrations/telegram/notifications`, {
        [notifId]: enabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => ({ ...prev, [notifId]: enabled }));
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const copyBotLink = () => {
    navigator.clipboard.writeText(`https://t.me/${botInfo?.username || 'OfficiumConnectBot'}`);
    toast.success('Bot link copied!');
  };

  const formatTime = (ts) => {
    const date = new Date(ts);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to App Hub
        </Button>
        
        <Card className="max-w-lg mx-auto">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-4">
              <Send className="h-8 w-8 text-sky-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Connect Telegram</h2>
            <p className="text-slate-500 mb-6">
              Receive recruitment notifications via Telegram bot and send announcements to your team.
            </p>

            <div className="mb-6 p-4 bg-slate-50 rounded-lg text-left">
              <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Bot className="h-4 w-4" />
                Setup Instructions
              </h4>
              <ol className="space-y-2 text-sm text-slate-600 list-decimal list-inside">
                <li>Add our bot to your Telegram group or start a chat</li>
                <li>Send <code className="bg-slate-200 px-1 rounded">/start</code> to the bot</li>
                <li>The bot will give you a Chat ID</li>
                <li>Enter the Chat ID below</li>
              </ol>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1 text-left">Chat ID</label>
                <Input
                  placeholder="Enter your Telegram Chat ID"
                  value={chatId}
                  onChange={(e) => setChatId(e.target.value)}
                />
              </div>
              <Button onClick={handleConnect} className="w-full bg-sky-500 hover:bg-sky-600" disabled={!chatId}>
                <Send className="h-4 w-4 mr-2" />
                Connect Telegram
              </Button>
            </div>

            <div className="mt-6 pt-4 border-t">
              <p className="text-xs text-slate-500">
                Bot: @OfficiumConnectBot
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="telegram-integration">
      {/* Header */}
      <div className="p-4 border-b bg-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center">
            <Send className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-semibold text-slate-800">Telegram</h1>
            <p className="text-xs text-slate-500">@{botInfo?.username || 'OfficiumConnectBot'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={copyBotLink}>
            <Copy className="h-4 w-4 mr-2" />
            Copy Bot Link
          </Button>
          <Button variant="outline" onClick={() => setShowSettings(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button onClick={() => setShowBroadcast(true)}>
            <Megaphone className="h-4 w-4 mr-2" />
            Broadcast
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Subscribers */}
        <div className="w-72 border-r bg-slate-50 p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-700">Subscribers</h3>
            <Badge variant="outline">{subscribers.length}</Badge>
          </div>
          <div className="space-y-2">
            {subscribers.map(sub => (
              <div key={sub.id} className="p-3 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    sub.type === 'group' ? 'bg-blue-100' : 'bg-green-100'
                  }`}>
                    {sub.type === 'group' ? (
                      <Users className="h-4 w-4 text-blue-600" />
                    ) : (
                      <UserPlus className="h-4 w-4 text-green-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-700 text-sm truncate">{sub.name}</p>
                    <p className="text-xs text-slate-500">
                      {sub.type === 'group' ? `${sub.members} members` : 'Private chat'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Messages */}
        <div className="flex-1 flex flex-col">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-slate-700">Recent Notifications Sent</h3>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {recentMessages.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <MessageCircle className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No messages sent yet</p>
              </div>
            ) : (
              recentMessages.map(msg => (
                <div key={msg.id} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-sky-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-slate-700">{msg.text}</p>
                      <p className="text-xs text-slate-500 mt-1">{formatTime(msg.time)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Broadcast Dialog */}
      <Dialog open={showBroadcast} onOpenChange={setShowBroadcast}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5" />
              Send Broadcast
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-4">
              Send a message to all {subscribers.length} subscribed chats/groups.
            </p>
            <Textarea
              placeholder="Type your announcement..."
              value={broadcastMessage}
              onChange={(e) => setBroadcastMessage(e.target.value)}
              rows={5}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBroadcast(false)}>Cancel</Button>
            <Button onClick={handleBroadcast} disabled={sending || !broadcastMessage.trim()}>
              {sending ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send to All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Telegram Notifications
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {TELEGRAM_NOTIFICATIONS.map(notif => (
              <div key={notif.id} className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-slate-700">{notif.name}</p>
                  <p className="text-xs text-slate-500">{notif.description}</p>
                </div>
                <Switch
                  checked={notifications[notif.id] ?? notif.default}
                  onCheckedChange={(checked) => handleUpdateNotifications(notif.id, checked)}
                />
              </div>
            ))}
          </div>
          <DialogFooter className="flex justify-between">
            <Button variant="destructive" onClick={handleDisconnect}>
              Disconnect
            </Button>
            <Button onClick={() => setShowSettings(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TelegramIntegration;
